import { datadogLogs } from "@datadog/browser-logs";

import type { IEventSessionState } from "@reducers/eventSession";

import type { IPaymentParam } from "@hooks/usePayment";

/**
 * wishlist 저장
 * tune_customer_wishlist
 */
export const fetchWishlist = async ({
  isRemove,
  wishlist,
  customerAccessToken,
  customerId,
}: {
  isRemove: boolean;
  wishlist: string[];
  customerAccessToken: string;
  customerId: string;
}): Promise<{ updated_wishlist: string } | undefined> => {
  try {
    const url = `https://bntzui3exu3ko4qt6obum374li0gmubw.lambda-url.ap-northeast-2.on.aws/${
      isRemove ? "" : "is_add=true"
    }`;

    const response = await fetch(url, {
      method: "POST",
      body: JSON.stringify({ wishlist, customerAccessToken, customer_id: customerId }),
    }).then((res) => res);
    if (response.status === 200) {
      const data = await response.json();
      return data;
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    datadogLogs.logger.error("[LAMBDA][tune_customer_wishlist] Failed to fetch", { message });
    throw new Error(`[LAMBDA][tune_customer_wishlist] Failed to fetch : ${message}`);
  }
};

/**
 * 본인 인증 저장
 * tune_customer_update
 */
export const fetchUserVerify = async (param: {
  phone: string;
  firstName: string;
  lastName: string;
  birth: string;
  gender: string;
  acceptsMarketing: boolean;
  customerId: string;
}) => {
  try {
    const url = "https://65ldlmd77z5m5ri6thry2rn3la0lnljb.lambda-url.ap-northeast-2.on.aws/";
    const response = await fetch(url, { method: "POST", body: JSON.stringify(param) }).then((res) => res);
    if (response.status === 200) {
      return response.json();
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    datadogLogs.logger.error("[LAMBDA][tune_customer_update] Failed to fetch", { message });
    throw new Error(`[LAMBDA][tune_customer_update] Failed to fetch : ${message}`);
  }
};

/**
 * 주문 취소
 * tune_order_cancel
 */
export const fetchCancelOrder = async ({
  customerId,
  orderId,
  amount,
}: {
  customerId: string;
  orderId: string;
  amount: string;
}) => {
  try {
    const url = `https://4qr2m4vxiosv5xqp4d6vno7cmu0uaqkt.lambda-url.ap-northeast-2.on.aws/?customer_id=${customerId}&order_id=${orderId}&amount=${amount}`;
    const response = await fetch(url).then((res) => res);
    if (response.status === 200) {
      return response;
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    datadogLogs.logger.error("[LAMBDA][tune_order_cancel] Failed to fetch", { message });
    throw new Error(`[LAMBDA][tune_order_cancel] Failed to fetch : ${message}`);
  }
};

/**
 * 소셜 로그인에서 탈퇴 회원 제거
 * tune_metaobject_update
 */
export const fetchRemoveSocialList = async (email: string) => {
  try {
    const url = "https://wjjigrj3bp3qohdpo5hqx7l7xu0ydrrw.lambda-url.ap-northeast-2.on.aws/?is_remove=true";
    const response = await fetch(url, { method: "POST", body: JSON.stringify({ email }) }).then((res) => res);

    if (response.status === 200) {
      return response;
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    datadogLogs.logger.error("[LAMBDA][tune_metaobject_update] Failed to fetch", { message });
    throw new Error(`[LAMBDA][tune_metaobject_update] Failed to fetch : ${message}`);
  }
};

interface IFetchVariantBackup {
  quantity: { S?: string; N?: string };
  variantId: { N: string };
}

/**
 * 백업해둔 variant
 * tune_variant_backup
 */
export const fetchVariantBackup = async (keyList: { variantId: { N: string } }[]) => {
  try {
    const url = `https://obx2xxf5azbw4iqulxfzyplisu0gabft.lambda-url.ap-northeast-2.on.aws/`;
    const response = await fetch(url, { method: "POST", body: JSON.stringify({ keyList }) }).then((res) => res);
    if (response.status === 200) {
      return (await response.json()) as IFetchVariantBackup[];
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    datadogLogs.logger.error("[LAMBDA][tune_variant_backup] Failed to fetch", { message });
    throw new Error(`[LAMBDA][tune_variant_backup] Failed to fetch : ${message}`);
  }
};

export interface IFetchVerify {
  phone: string;
  name: string;
  birthday: string;
  gender: string;
}

/**
 * tune_certification
 */
export const fetchVerify = async (uid: string): Promise<IFetchVerify | undefined> => {
  try {
    const url = "https://mon5z4ep7lyrhibfaocpirfzzq0kfcsw.lambda-url.ap-northeast-2.on.aws/";
    const response = await fetch(url, { method: "POST", body: JSON.stringify({ imp_uid: uid }) });
    if (response.status === 200) {
      return (await response.json()) as IFetchVerify;
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    datadogLogs.logger.error("[LAMBDA][tune_certification] Failed to fetch", { message });
    throw new Error(`[LAMBDA][tune_certification] Failed to fetch : ${message}`);
  }
};

interface IFetchSocialLogin {
  isCustomer: boolean;
  email: string;
  password: string;
}

/**
 * social Login
 * tune_social_login
 */
export const fetchSocialLogin = async (param: string) => {
  try {
    const url = `https://x6utpgtvybn27wugmn3huqn7340lkyla.lambda-url.ap-northeast-2.on.aws/?source${param}`;
    const response = await fetch(url);
    if (response.status === 200) {
      return (await response.json()) as IFetchSocialLogin;
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    datadogLogs.logger.error("[LAMBDA][tune_social_login] Failed to fetch", { message });
    throw new Error(`[LAMBDA][tune_social_login] Failed to fetch : ${message}`);
  }
};

interface IFetchPayment {
  orderId: string;
  orderNumber: string;
  status: number;
  message?: string;
}

/**
 * 결제 승인 + 주문 생성
 * tune_order_process
 */
export const fetchPayment = async (
  param: IPaymentParam & { paymentKey: string; orderId: string; brandPay: string; amount: number },
): Promise<IFetchPayment | undefined> => {
  try {
    // tune_order_process
    const url = `https://vxkulvgpjwdes4bqdl4ftk5apu0bqsbo.lambda-url.ap-northeast-2.on.aws/${
      param.brandPay === "BRANDPAY" ? "?brandPay=True" : ""
    }`;
    const response = await fetch(url, { method: "POST", body: JSON.stringify(param) });
    const res = await response.json();
    return { ...res, status: response.status } as IFetchPayment;
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    datadogLogs.logger.error("[LAMBDA][tune_order_process] Failed to fetch", { message });
    throw new Error(`[LAMBDA][tune_order_process] Failed to fetch : ${message}`);
  }
};

interface IFetchMetaData {}

/**
 * 소셜 로그인 메타데이터 업데이트
 * tune_social_login_update_meta_data
 */
export const fetchMetaData = async ({
  email,
  customerId,
  password,
  param,
}: {
  email: string;
  customerId: string;
  password: string;
  param: string;
}) => {
  try {
    const url = `https://ldbdg5jv7hfwjwnvwl73txmney0tjfsy.lambda-url.ap-northeast-2.on.aws/?source${param}`;
    const response = await fetch(url, { method: "POST", body: JSON.stringify({ email, customerId, password }) });
    if (response.status === 200) {
      return (await response.json()) as IFetchMetaData;
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    datadogLogs.logger.error("[LAMBDA][tune_social_login_update_meta_data] Failed to fetch", { message });
    throw new Error(`[LAMBDA][tune_social_login_update_meta_data] Failed to fetch : ${message}`);
  }
};

export interface IRunningInfo {
  message: string;
  data: {
    index: number;
    customer_id: string;
    options: { name: string; value: string }[];
    phone: string;
    instagram: string;
    email: string;
    eventStatus: string;
    imageUrl: string;
    handle: string;
    title: string;
    page: string;
  }[];
}

/**
 * community gatherings 신청
 * tune_running_db
 */
export const fetchRunningInsert = async (data: {
  index: number;
  customer_id: string;
  options?: { name: string; value: string }[];
  phone: string;
  instagram: string;
  email: string;
  eventStatus: string;
  imageUrl: string;
  handle: string;
  title: string;
}) => {
  try {
    const url = "https://jbp4aiclthsgeffhx6ft7pk6qq0euzty.lambda-url.ap-northeast-2.on.aws/";
    const response = await fetch(url, { method: "POST", body: JSON.stringify(data) });
    if (response.status === 200) {
      return (await response.json()) as IRunningInfo;
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    datadogLogs.logger.error("[LAMBDA][tune_running_db] Failed to fetch", { message });
    throw new Error(`[LAMBDA][tune_running_db] Failed to fetch : ${message}`);
  }
};

/**
 * community gatherings 조회
 * tune_running_db
 */
export const fetchRunningInfo = async (customer_id: string, handle?: string) => {
  try {
    const url = `https://jbp4aiclthsgeffhx6ft7pk6qq0euzty.lambda-url.ap-northeast-2.on.aws/?customer_id=${customer_id}${handle ? `&handle=${handle}` : ""}`;
    const response = await fetch(url);
    if (response.status === 200) {
      return (await response.json()) as IRunningInfo;
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    datadogLogs.logger.error("[LAMBDA][tune_running_db] Failed to fetch", { message });
    throw new Error(`[LAMBDA][tune_running_db] Failed to fetch : ${message}`);
  }
};

/**
 * community gatherings 수정
 * tune_running_db
 */
export const fetchCancelRunningInfo = async (data: IEventSessionState) => {
  try {
    const url = "https://jbp4aiclthsgeffhx6ft7pk6qq0euzty.lambda-url.ap-northeast-2.on.aws/";
    const response = await fetch(url, { method: "POST", body: JSON.stringify({ ...data, type: "cancel" }) });
    if (response.status === 200) {
      return (await response.json()) as IRunningInfo;
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    datadogLogs.logger.error("[LAMBDA][tune_running_db] Failed to fetch", { message });
    throw new Error(`[LAMBDA][tune_running_db] Failed to fetch : ${message}`);
  }
};

export interface ICheckin {
  index: number;
  handle: string;
  customer_id: string;
  phone: string;
  name: string;
  instagram?: string;
  option1?: { key: string; value: string };
  option2?: { key: string; value: string };
  type?: string;
  imageUrl?: string;
  title?: string;
}

/**
 * 체크인 추가
 * tune_checkin
 */
export const fetchCheckin = async (param: ICheckin) => {
  try {
    const url = "https://f7yje46cqcuk65ced7dcoxle740gviql.lambda-url.ap-northeast-2.on.aws/";
    const response = await fetch(url, { method: "POST", body: JSON.stringify(param) });
    if (response.status === 200) {
      return (await response.json()) as { message: string; data: ICheckin[] };
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    datadogLogs.logger.error("[LAMBDA][tune_checkin] Failed to fetch", { message });
    throw new Error(`[LAMBDA][tune_checkin] Failed to fetch : ${message}`);
  }
};

/**
 * 체크인 조회
 * tune_checkin
 */
export const fetchCheckinInfo = async ({ handle = "", customer_id }: { handle?: string; customer_id: string }) => {
  try {
    const url = `https://f7yje46cqcuk65ced7dcoxle740gviql.lambda-url.ap-northeast-2.on.aws/?customer_id=${customer_id}&handle=${handle}`;
    const response = await fetch(url);
    if (response.status === 200) {
      return (await response.json()) as { message: string; data: ICheckin[] };
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    datadogLogs.logger.error("[LAMBDA][tune_checkin] Failed to fetch", { message });
    throw new Error(`[LAMBDA][tune_checkin] Failed to fetch : ${message}`);
  }
};
